import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  CardActions,
  CardContent,
  Chip,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { formatDateTime } from 'helpers/formaters';
import { formatSize, packageStatus, packageStatusColor } from 'common/packages';

const COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'version', label: 'Version' },
  { key: 'type', label: 'Type' },
  { key: 'status', label: 'Status' },
  // Not sortable: the shared comparator coerces with `a[orderBy] || ''`, which
  // turns a count of 0 — the value that matters most here — into an empty string.
  { key: 'access', label: 'Access', sortable: false },
  { key: 'size', label: 'Size', align: 'right' },
  { key: 'updated_at', label: 'Updated' },
];

// access_count is absent until the API ships it. Zero grants and "not reported"
// are very different facts and must not render the same.
const AccessCell = ({ pkg }) => {
  if (pkg.is_public) {
    return (
      <Typography variant="body2" color="text.secondary">
        Public
      </Typography>
    );
  }
  const count = typeof pkg.access_count === 'number' ? pkg.access_count : null;
  if (count === null) {
    return (
      <Chip size="small" variant="outlined" icon={<LockOutlinedIcon />} label="Restricted" />
    );
  }
  return (
    <Chip
      size="small"
      icon={<LockOutlinedIcon />}
      color={count === 0 ? 'error' : 'default'}
      variant={count === 0 ? 'filled' : 'outlined'}
      label={count === 0 ? 'No accounts' : `${count} account${count === 1 ? '' : 's'}`}
    />
  );
};

const PackageTable = (props) => {
  const {
    application,
    loading,
    packages,
    total,
    page,
    rowsPerPage,
    sort,
    onSortChange,
    onPageChange,
    onRowsPerPageChange,
  } = props;

  const handleSort = (key) => {
    const isSame = sort.by === key;
    onSortChange({
      by: key,
      direction: isSame && sort.direction === 'desc' ? 'asc' : 'desc',
    });
  };

  return (
    <>
      {loading && <LinearProgress />}
      <CardContent sx={{ p: 0 }}>
        <PerfectScrollbar>
          <Box sx={{ minWidth: 1040 }}>
            <Table>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align}
                      sx={{ whiteSpace: 'nowrap' }}
                      sortDirection={sort.by === column.key ? sort.direction : false}
                    >
                      {column.sortable === false ? (
                        column.label
                      ) : (
                        <TableSortLabel
                          active={sort.by === column.key}
                          direction={sort.by === column.key ? sort.direction : 'asc'}
                          onClick={() => handleSort(column.key)}
                        >
                          {column.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody sx={{ opacity: loading ? 0.5 : 1 }}>
                {packages.map((pkg) => {
                  const status = packageStatus(pkg);
                  return (
                    <TableRow hover key={pkg.uuid}>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/packages/${application}/${pkg.uuid}`}
                        >
                          {pkg.name}
                        </Link>
                        {pkg.label && pkg.label !== pkg.name && (
                          <Typography variant="body2" color="text.secondary">
                            {pkg.label}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{pkg.version}</TableCell>
                      <TableCell>{pkg.type}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={status}
                          color={packageStatusColor(status)}
                          variant={status === 'Draft' ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <AccessCell pkg={pkg} />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {formatSize(pkg.size)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(pkg.updated_at) || '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!loading && packages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length}>
                      <Typography variant="body2" color="text.secondary">
                        No packages match the current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </PerfectScrollbar>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={(event, value) => onPageChange(value)}
          onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
          showFirstButton
          showLastButton
        />
      </CardActions>
    </>
  );
};

PackageTable.propTypes = {
  application: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  packages: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  sort: PropTypes.object.isRequired,
  total: PropTypes.number.isRequired,
};

export default PackageTable;
